import { useEffect, useRef } from 'react';

export type KeyHandler = () => void;
export type KeyMap = Record<string, KeyHandler>;

interface UseKeyboardOptions {
  enabled?: boolean;
}

const CHORD_TIMEOUT_MS = 500;

// Detect Mac platform once at module load. We check navigator.platform first
// (the traditional source) and fall back to userAgent for environments where
// platform is empty/locked (some recent Chromium versions, certain headless
// runtimes). jsdom returns 'win32'/'linux' style platform strings, so tests
// resolve `mod` to ctrlKey there — but our implementation accepts EITHER
// metaKey OR ctrlKey for `mod+key` combos, so cross-platform UX still works.
function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform ?? '';
  const ua = navigator.userAgent ?? '';
  return platform.includes('Mac') || ua.includes('Mac');
}

interface ParsedBinding {
  /** Original key string from the KeyMap (used to look up the handler). */
  raw: string;
  kind: 'single' | 'mod' | 'chord';
  /** For 'single' and 'mod': the final key (lowercase). */
  key?: string;
  /** For 'mod': whether mod modifier is required. */
  needsMod?: boolean;
  /** For 'chord': the two keys (lowercase). */
  chordFirst?: string;
  chordSecond?: string;
}

function parseBinding(raw: string): ParsedBinding {
  // Two-key chord: contains a space (e.g. "g s").
  if (raw.includes(' ')) {
    const parts = raw.split(/\s+/).map((p) => p.toLowerCase());
    return {
      raw,
      kind: 'chord',
      chordFirst: parts[0],
      chordSecond: parts[1],
    };
  }
  // Mod combo: contains '+' (e.g. "mod+e").
  if (raw.includes('+')) {
    const parts = raw.split('+').map((p) => p.trim().toLowerCase());
    const needsMod = parts.includes('mod');
    const key = parts.filter((p) => p !== 'mod').join('+');
    return { raw, kind: 'mod', key, needsMod };
  }
  // Single key.
  return { raw, kind: 'single', key: raw.toLowerCase() };
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((target as HTMLElement).isContentEditable) return true;
  return false;
}

/**
 * Hook to register keyboard shortcuts.
 *
 * @example
 *   useKeyboard({ j: onNext, k: onPrev });
 *   useKeyboard({ 'mod+e': onExport, 'mod+b': onBreakdown });
 *   useKeyboard({ 'g s': onGoScenes });
 *
 * Behavior:
 *  - `mod` resolves to Cmd on Mac, Ctrl elsewhere. Our matcher accepts EITHER
 *    metaKey or ctrlKey so the hook works correctly across platforms regardless
 *    of which physical modifier the user pressed.
 *  - Single-key and chord handlers are SKIPPED when an input/textarea/contenteditable
 *    has focus (so typing 'j' in a textarea inserts 'j' instead of firing onNext).
 *    Mod combos still fire inside inputs — `⌘E` in a textarea must still export.
 *  - Two-key chords wait up to 500ms for the second key. If a non-matching key
 *    arrives first OR the timeout expires, the chord is abandoned silently.
 *  - When `enabled: false`, no listeners attach.
 */
export function useKeyboard(map: KeyMap, options: UseKeyboardOptions = {}): void {
  const { enabled = true } = options;

  // Keep the map in a ref so the effect doesn't re-run on every render when
  // callers pass a fresh object literal each render (the common case).
  const mapRef = useRef<KeyMap>(map);
  mapRef.current = map;

  // Chord-pending state held in a ref so it survives re-renders.
  const chordRef = useRef<{ firstKey: string; expiresAt: number } | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent) => {
      const currentMap = mapRef.current;
      const bindings = Object.keys(currentMap).map(parseBinding);
      const editable = isEditableTarget(event.target);
      const eventKey = event.key.toLowerCase();
      const hasMod = event.metaKey || event.ctrlKey;

      // 1. Mod combos fire FIRST and ALWAYS (even inside editable targets).
      if (hasMod) {
        for (const binding of bindings) {
          if (binding.kind !== 'mod') continue;
          if (binding.needsMod && binding.key === eventKey) {
            event.preventDefault();
            currentMap[binding.raw]?.();
            // A mod combo also cancels any pending chord — the user is doing
            // something else entirely.
            chordRef.current = null;
            return;
          }
        }
        // A modifier press with no matching binding shouldn't start/continue chords.
        return;
      }

      // From here on: no mod modifier pressed.

      // 2. If we're inside an editable target, do not run single-key or chord
      // handlers — let the input receive its own keystrokes.
      if (editable) return;

      // 3. Chord continuation: if a chord is pending and within the window,
      // try to complete it.
      const pending = chordRef.current;
      if (pending) {
        const now = Date.now();
        if (now <= pending.expiresAt) {
          for (const binding of bindings) {
            if (binding.kind !== 'chord') continue;
            if (binding.chordFirst === pending.firstKey && binding.chordSecond === eventKey) {
              event.preventDefault();
              chordRef.current = null;
              currentMap[binding.raw]?.();
              return;
            }
          }
        }
        // Pending chord did not complete with this key (either timed out or
        // mismatched second key). Abandon it and fall through so this key can
        // still trigger a single-key handler or start a fresh chord.
        chordRef.current = null;
      }

      // 4. Try single-key handlers.
      for (const binding of bindings) {
        if (binding.kind !== 'single') continue;
        if (binding.key === eventKey) {
          event.preventDefault();
          currentMap[binding.raw]?.();
          return;
        }
      }

      // 5. Maybe this key starts a chord. If any registered chord has its
      // first key match eventKey, set chord-pending state.
      const startsChord = bindings.some(
        (b) => b.kind === 'chord' && b.chordFirst === eventKey
      );
      if (startsChord) {
        chordRef.current = {
          firstKey: eventKey,
          expiresAt: Date.now() + CHORD_TIMEOUT_MS,
        };
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [enabled]);
}

// Exposed for diagnostic / future use (e.g. rendering hint overlays that
// label `mod` as `⌘` vs `Ctrl`).
export const IS_MAC = isMacPlatform();
